import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import Button from "./Button";
import D3EFeature from "../models/D3EFeature";
import MaterialIcons from "../icons/MaterialIcons";
import TextView from "./TextView";
import IconView from "./IconView";

type _ReviewFeatureViewOnChangeView = (isMobileView: boolean) => void;

type _MobileViewOnPressed = (d3eState: ReviewFeatureViewRefs) => void;

type _TabletViewOnPressed = (d3eState: ReviewFeatureViewRefs) => void;

export interface ReviewFeatureViewProps extends BaseUIProps {
  key?: string;
  obj: D3EFeature;
  onChangeView: _ReviewFeatureViewOnChangeView;
}
/// To store state data for ReviewFeatureView
class ReviewFeatureViewRefs {
  public mobileView: MobileViewState = new MobileViewState();
  public tabletView: TabletViewState = new TabletViewState();
}

interface TabletViewWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: ReviewFeatureViewRefs;
  _isWebViewHandler?: _TabletViewOnPressed;
  isWebView: boolean;
}

class TabletViewState extends ObjectObservable {
  private _disable: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _TabletViewWithState extends ObservableComponent<TabletViewWithStateProps> {
  tabletViewFocusNode: ui.FocusNode = new ui.FocusNode();
  public constructor(props: TabletViewWithStateProps) {
    super(props);

    this.initState();
  }
  public get isWebView(): boolean {
    return this.props.isWebView;
  }
  public get tabletView(): TabletViewState {
    return this.props.d3eState.tabletView;
  }
  public get d3eState(): ReviewFeatureViewRefs {
    return this.props.d3eState;
  }
  public get _isWebViewHandler(): _TabletViewOnPressed {
    return this.props._isWebViewHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("tabletView", null, this.tabletView);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["isWebView", "tabletView"], this.rebuild);
  }
  public dispose(): void {
    super.dispose();
  }
  public render(): ReactNode {
    return Button({
      decoration: this.isWebView
        ? new ui.BoxDecoration({
            border: ui.Border.all({
              color: new ui.Color(0xff3c3e49),
              width: 0.5,
            }),
            color: new ui.Color(0xffecedf0),
          })
        : new ui.BoxDecoration({
            border: ui.Border.all({
              color: new ui.Color(0xff3c3e49),
              width: 0.5,
            }),
            color: new ui.Color(0xffffffff),
          }),
      disable: this.tabletView.disable,
      onPressed: () => {
        this._isWebViewHandler(this.d3eState);
      },
      onFocusChange: (val) => {},
      child: IconView({
        icon: MaterialIcons.desktop_mac,
        color: new ui.Color(0xff3c3e49),
        size: 15,
        className: "xc4f",
      }),
    });
  }
}
function TabletViewWithState(props: TabletViewWithStateProps) {
  return React.createElement(_TabletViewWithState, props);
}

interface MobileViewWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: ReviewFeatureViewRefs;
  _isMobileViewHandler?: _MobileViewOnPressed;
  isMobileView: boolean;
}

class MobileViewState extends ObjectObservable {
  private _disable: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _MobileViewWithState extends ObservableComponent<MobileViewWithStateProps> {
  mobileViewFocusNode: ui.FocusNode = new ui.FocusNode();
  public constructor(props: MobileViewWithStateProps) {
    super(props);

    this.initState();
  }
  public get isMobileView(): boolean {
    return this.props.isMobileView;
  }
  public get mobileView(): MobileViewState {
    return this.props.d3eState.mobileView;
  }
  public get d3eState(): ReviewFeatureViewRefs {
    return this.props.d3eState;
  }
  public get _isMobileViewHandler(): _MobileViewOnPressed {
    return this.props._isMobileViewHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("mobileView", null, this.mobileView);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["isMobileView", "mobileView"], this.rebuild);
  }
  public dispose(): void {
    super.dispose();
  }
  public render(): ReactNode {
    return ui.Container({
      margin: ui.EdgeInsets.all(5.0, new Map()),
      child: Button({
        decoration: this.isMobileView
          ? new ui.BoxDecoration({
              border: ui.Border.all({
                color: new ui.Color(0xff3c3e49),
                width: 0.5,
              }),
              color: new ui.Color(0xffecedf0),
            })
          : new ui.BoxDecoration({
              border: ui.Border.all({
                color: new ui.Color(0xff3c3e49),
                width: 0.5,
              }),
              color: new ui.Color(0xffffffff),
            }),
        disable: this.mobileView.disable,
        onPressed: () => {
          this._isMobileViewHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: IconView({
          icon: MaterialIcons.phone_iphone,
          color: new ui.Color(0xff3c3e49),
          size: 15,
          className: "xeba",
        }),
      }),
      className: "xbb",
    });
  }
}
function MobileViewWithState(props: MobileViewWithStateProps) {
  return React.createElement(_MobileViewWithState, props);
}

class _ReviewFeatureViewState extends ObservableComponent<ReviewFeatureViewProps> {
  static defaultProps = { obj: null, onChangeView: null };
  d3eState: ReviewFeatureViewRefs = new ReviewFeatureViewRefs();
  isMobileView: boolean = false;
  isWebView: boolean = false;
  public constructor(props: ReviewFeatureViewProps) {
    super(props);

    this.initState();
  }
  public get obj(): D3EFeature {
    return this.props.obj;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("obj", this.props.obj);

    this.on(
      [
        "isMobileView",
        "isWebView",
        "obj",
        "obj.androidScreenshot",
        "obj.bundle",
        "obj.bundle.title",
        "obj.description",
        "obj.title",
        "obj.webScreenshot",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: ReviewFeatureViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.obj !== this.props.obj) {
      this.updateObservable("obj", prevProps.obj, this.props.obj);

      this.fire("obj", this);
    }
  }
  public setIsMobileView(val: boolean): void {
    let isValChanged: boolean = this.isMobileView !== val;

    if (!isValChanged) {
      return;
    }

    this.isMobileView = val;

    this.fire("isMobileView", this);
  }
  public setIsWebView(val: boolean): void {
    let isValChanged: boolean = this.isWebView !== val;

    if (!isValChanged) {
      return;
    }

    this.isWebView = val;

    this.fire("isWebView", this);
  }
  public render(): ReactNode {
    return ui.Row({
      mainAxisAlignment: ui.MainAxisAlignment.center,
      crossAxisAlignment: ui.CrossAxisAlignment.center,
      children: [
        ui.Column({
          crossAxisAlignment: ui.CrossAxisAlignment.center,
          mainAxisAlignment: ui.MainAxisAlignment.start,
          children: [
            ui.Row({
              children: [
                MobileViewWithState({
                  d3eState: this.d3eState,
                  _isMobileViewHandler: this.isMobileViewHandler,
                  isMobileView: this.isMobileView,
                  key: "0",
                }),
                TabletViewWithState({
                  d3eState: this.d3eState,
                  _isWebViewHandler: this.isWebViewHandler,
                  isWebView: this.isWebView,
                  key: "1",
                }),
              ],
              className: "x9e6",
              key: "0",
            }),
          ],
          className: "xa41 vc v",
          key: "0",
        }),
        ui.Column({
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          children: [
            ui.Container({
              clipBehavior: ui.Clip.hardEdge,
              child: ui.NetworkImage({
                url: this.isMobileView
                  ? this.obj.androidScreenshot?.downloadUrl ?? ""
                  : this.obj.webScreenshot?.downloadUrl ?? "",
                fit: ui.BoxFit.contain,
                className: "hc vc",
              }),
              className: "x55e hc vc",
              key: "0",
            }),
          ],
          className: "x9ab vc v",
          key: "1",
        }),
        ui.Column({
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          mainAxisAlignment: ui.MainAxisAlignment.start,
          children: [
            ui.Row({
              crossAxisAlignment: ui.CrossAxisAlignment.center,
              mainAxisAlignment: ui.MainAxisAlignment.start,
              children: [
                TextView({
                  data: this.obj.title,
                  textAlign: ui.TextAlign.center,
                  style: new ui.TextStyle({ fontWeight: ui.FontWeight.w600 }),
                  className: "xfd",
                  key: "0",
                }),
              ],
              className: "xc51 hc h",
              key: "0",
            }),
            TextView({
              data: this.obj.bundle !== null ? this.obj.bundle.title : "",
              style: new ui.TextStyle({
                fontSize: 14,
                color: new ui.Color(0xff9b9fb1),
              }),
              className: "xd0e hc",
              key: "1",
            }),
            TextView({
              data: this.obj.description,
              style: new ui.TextStyle({
                fontSize: 14,
                color: new ui.Color(0xff9b9fb1),
              }),
              className: "x1c7 hc",
              key: "2",
            }),
          ],
          className: "x8b5 hc vc v",
          key: "2",
        }),
      ],
      className: ui.join(
        this.props.className,
        "ReviewFeatureView x4c hc vc h v"
      ),
      ...copyBaseUIProps(this.props),
    });
  }
  public isMobileViewHandler = (d3eState: ReviewFeatureViewRefs): void => {
    this.setIsMobileView(true);

    this.setIsWebView(false);

    this.onChangeView(this.isMobileView);
  };
  public isWebViewHandler = (d3eState: ReviewFeatureViewRefs): void => {
    this.setIsMobileView(false);

    this.setIsWebView(true);

    this.onChangeView(this.isMobileView);
  };
  public get onChangeView(): _ReviewFeatureViewOnChangeView {
    return this.props.onChangeView;
  }
  public get mobileView() {
    return this.d3eState.mobileView;
  }
  public get tabletView() {
    return this.d3eState.tabletView;
  }
}
export default function ReviewFeatureView(props: ReviewFeatureViewProps) {
  return React.createElement(_ReviewFeatureViewState, {
    ..._ReviewFeatureViewState.defaultProps,
    ...props,
  });
}
